import {
  formatDateToLocal,
  formatObligationInterval,
  type IntervalVariantConfirmation,
  type LegalDocumentComplianceCounts,
  type LegalDocumentSiteSummary,
  type LegalObligationAnnualPeriod,
  type LegalObligationApplicability,
  type LegalObligationCurrentPeriod,
  type LegalObligationEvidenceKindValue,
  type LegalObligationIntervalVariantView,
  type LegalObligationResultView,
  type LegalObligationSubjectExcludedReason,
  type ObligationInstanceStatus,
  type ObligationPeriodSkipReason,
  type ObligationRecurrenceRule,
} from '@safetyhub/shared';

/**
 * 대상이 업체 의무의 분모 밖인 이유 — 의무 목록(`SiteObligationsPage`)과 연간 격자(`AnnualGrid`)가 같은 문구를 쓴다.
 * 위탁(사용자 결정 2026-11-02 11:43 ⓑ — 스위치는 서버 `DELEGATED_SUBJECT_POLICY`) · 설비 판정 미완(②-c2 — 규격 값이 비어 판정이 안 선 설비)
 */
export const SUBJECT_EXCLUDED_LABEL: Record<LegalObligationSubjectExcludedReason, string> = {
  DELEGATED_TO_AGENCY: '위탁 — 직무교육은 수탁 기관의 의무(법 제32조① 5호)',
  EQUIPMENT_SPEC_MISSING: '확인 필요',
};

export const SUBJECT_EXCLUDED_HINT: Record<LegalObligationSubjectExcludedReason, string> = {
  DELEGATED_TO_AGENCY:
    '전문기관에 위탁한 직위입니다. 기관 종사자의 직무교육은 그 기관의 장이 이수하게 할 의무(산업안전보건법 제32조제1항제5호)라 이 사업장의 문서 항목으로 세지 않습니다.',
  EQUIPMENT_SPEC_MISSING:
    '기종은 골랐지만 규격 값이 비어 법정 안전검사 대상인지(또는 어느 주기인지) 판정되지 않았습니다. 현장 상세 「대상」에서 그 설비의 규격 값을 넣으면 기한이 생깁니다.',
};

/** 분모 밖 사유 문구 — 설비 판정 미완은 빠진 항목을 괄호로 붙인다(「확인 필요(정격하중)」 — 설계 2차 §2-2 「화면 표시」) */
export function subjectExcludedText(subject: {
  excludedReason: LegalObligationSubjectExcludedReason;
  pendingFieldLabels?: string[];
}): string {
  const base = SUBJECT_EXCLUDED_LABEL[subject.excludedReason];
  return subject.excludedReason === 'EQUIPMENT_SPEC_MISSING' && subject.pendingFieldLabels?.length
    ? `${base}(${subject.pendingFieldLabels.join(' · ')})`
    : base;
}

/**
 * 증빙 종류(9-d) — 서버 enum `LegalObligationEvidenceKind`의 세 값. 「사건 없음」은 **이행으로 센다**(L13)
 * 는 뜻을 라벨이 그대로 말한다(「안 했음」으로 읽히면 안 된다).
 */
export const EVIDENCE_KIND_LABEL: Record<LegalObligationEvidenceKindValue, string> = {
  INTERNAL_REPORT: 'SafetyHub 문서',
  EXTERNAL_FILE: '외부 발급 파일',
  NO_EVENT_IN_PERIOD: '해당 기간 사건 없음',
};

/**
 * 법정 서류 관리 화면의 표시 문구 — **순수 모듈**(API 클라이언트 `legalDocuments.ts`와 가른 이유는 그 파일 doc).
 *
 * `Record<Union, string>`으로 둔 것은 **값 누락을 tsc가 막게** 하려는 것이다 — shared 유니온에 값이
 * 늘면 이 표가 컴파일 단계에서 깨진다. ⚠️ 그것은 «누락»만 막고 «문구가 옳은가»는 재지 않는다.
 */

// ⚠️ 주기·담당 주체 라벨은 **여기 없다** — 2026-10-29에 shared `features/legal-obligation-catalog.ts`의
//    `LEGAL_OBLIGATION_RECURRENCE_LABEL`·`LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL`로 옮겼다(admin 카탈로그
//    화면이 같은 표를 읽는다 — 두 벌이면 고객 화면과 운영 화면이 다른 말을 한다). 여기 다시 만들지 말 것.

/**
 * 적용 판정 배지 — `UNKNOWN`만 그린다(`APPLIES`는 목록에 있는 것 자체가 그 뜻이라 null).
 * 사용자 결정(2026-10-28): 판정 재료가 없는 의무는 **포함하되 단정하지 않는다** — 그래서 「의무」가
 * 아니라 「확인 필요」라 말하고, 업체가 그 행에서 제외(해당 없음)로 닫을 수 있다.
 */
export const APPLICABILITY_BADGE: Record<
  LegalObligationApplicability,
  { label: string; hint: string } | null
> = {
  APPLIES: null,
  UNKNOWN: {
    label: '해당 여부 확인 필요',
    hint: '이 현장에 해당하는지 판단할 정보가 아직 없습니다. 해당하지 않으면 「해당 없음」으로 제외하세요.',
  },
};

export type InstanceStatusTone = 'slate' | 'rose' | 'emerald' | 'amber';

export const INSTANCE_STATUS_LABEL: Record<
  ObligationInstanceStatus,
  { label: string; tone: InstanceStatusTone }
> = {
  PENDING: { label: '대기', tone: 'slate' },
  OVERDUE: { label: '지연', tone: 'rose' },
  FULFILLED: { label: '완료', tone: 'emerald' },
  FULFILLED_LATE: { label: '늦은 완료', tone: 'amber' },
};

/**
 * 제외 사유 분류 — 서버 Prisma enum `SiteObligationExclusionReason`의 **사본**이다
 * (shared 계약이 `reasonCode: string`이라 유니온이 없다).
 *
 * ⛔ 손열거라 서버가 값을 더하면 **여기서 조용히 빠진다**(선택지에 없을 뿐 red가 없다) — 그 축은
 * `__tests__/legalDocumentLabels.test.ts`의 「서버 enum과 같은 집합」 케이스가 schema 텍스트를 읽어
 * 잰다. `OTHER`가 없는 것은 서버 enum의 결정이다(분류가 안 되는 제외가 새 분류의 신호다).
 */
export const EXCLUSION_REASON_CODES = [
  'EQUIPMENT_NOT_OWNED',
  'WORK_NOT_PERFORMED',
  'NO_CONTRACTED_WORK',
] as const;

export type ExclusionReasonCode = (typeof EXCLUSION_REASON_CODES)[number];

export const EXCLUSION_REASON_LABEL: Record<ExclusionReasonCode, { label: string; hint: string }> =
  {
    EQUIPMENT_NOT_OWNED: {
      label: '대상 설비를 보유하지 않음',
      hint: '안전검사 대상 설비·기계를 보유하지 않습니다.',
    },
    WORK_NOT_PERFORMED: {
      label: '대상 작업을 수행하지 않음',
      hint: '공정 자체가 없어 대상 작업을 영구히 하지 않습니다. 「이번 기간에 없었다」는 제외가 아닙니다.',
    },
    NO_CONTRACTED_WORK: {
      label: '도급·용역을 주지 않음',
      hint: '도급·용역·위탁을 주지 않습니다.',
    },
  };

/** 서버가 모르는 값을 보내도 화면이 코드 문자열을 그대로 드러내지 않게 한다 */
export function exclusionReasonLabel(code: string): string {
  return (
    (EXCLUSION_REASON_LABEL as Record<string, { label: string } | undefined>)[code]?.label ??
    '기타 사유'
  );
}

/**
 * 연간 격자에서 달력 칸을 그릴 수 없는 사유 — shared `ObligationPeriodSkipReason` 전수(`Record`라 값이 늘면
 * tsc가 막는다). 사유마다 닫는 쪽이 달라(조문 검증 · 사업장 입력 · 성질) 한 문구로 합치지 않는다.
 */
export const UNSCHEDULED_REASON_LABEL: Record<ObligationPeriodSkipReason, string> = {
  RECURRENCE_UNSET: '주기 확인 중 — 기간이 정해지면 칸이 표시됩니다',
  EVENT_DRIVEN: '사유 발생 시 — 사건이 생기면 문서 목록에서 「사건 등록」으로 기록합니다',
  ONE_TIME: '최초 1회 — 달력 기간이 없습니다',
  STANDING_DUTY: '상시 — 갖춰 두는 문서라 달력 기간이 없습니다',
  ROLLING_NO_ANCHOR: '기준일부터 도는 주기 — 문서 목록에서 마지막 실시일(또는 설치·배치일)을 입력하면 칸이 표시됩니다',
  // 2026-11-04 — 보수교육 ← 신규교육 이수일(시행규칙 제29조①). 입력 입구가 아니라 선행 의무의 문서·면제 등록이 닫는다
  PREREQUISITE_NOT_FULFILLED: '선행 교육(신규교육) 이수일부터 도는 주기 — 그 사람의 신규교육 문서·면제를 등록하면 칸이 표시됩니다',
  CONSTRUCTION_PERIOD_UNKNOWN: '공사 기간이 입력되지 않아 칸을 그릴 수 없습니다',
  PERIOD_RULE_INVALID: '주기 정보가 맞지 않습니다 — 운영사가 카탈로그를 고치면 칸이 표시됩니다',
};

/**
 * 사건 등록(9-i) 모달의 **기한 참고 문구** — 카탈로그 키별. ⛔ **안내만 하고 기본값으로 채우지 않는다**(사용자·팀장
 * 결정 2026-10-29): 기한의 정본은 사람이 넣은 값이고, 이 표는 「어디를 보면 되나」만 알려 준다. 키가 없으면 일반 안내다.
 */
export const EVENT_DEADLINE_REFERENCE: Record<string, string> = {
  'industrial-accident-report':
    '참고: 산업재해조사표는 재해 발생 후 1개월 이내 제출(산업안전보건법 시행규칙 제73조). 실제 기한을 확인해 입력하세요.',
  'corrective-order-records': '참고: 시정명령서에 적힌 이행 기한을 입력하세요.',
  // 선임 보고 3행(마이그레이션 `20261029180000_legal_obligation_appointment_report_rows` + 셋째 행 정정
  // `20261029190000_legal_obligation_appointment_report_fix_and_dismissal`) — 「사건일」은 선임·위탁(위촉)한 날이다
  'appointment-report-art-17':
    '참고: 안전관리자를 선임하거나 위탁한 날부터 14일 이내 제출(산업안전보건법 시행령 제16조제6항). 실제 기한을 확인해 입력하세요.',
  'appointment-report-art-18':
    '참고: 보건관리자를 선임하거나 위탁한 날부터 14일 이내 제출(산업안전보건법 시행령 제20조제3항). 실제 기한을 확인해 입력하세요.',
  'appointment-report-art-22':
    '참고: 산업보건의를 선임하거나 위촉한 날부터 14일 이내 제출(산업안전보건법 시행령 제29조제3항). 실제 기한을 확인해 입력하세요.',
  // 해임 보고 3행(같은 `20261029190000` 마이그레이션) — 「사건일」은 해임·위탁 해지(해촉)한 날이다
  'dismissal-report-art-17':
    '참고: 안전관리자를 해임하거나 위탁을 해지한 날부터 14일 이내 제출(산업안전보건법 시행령 제16조제7항). 실제 기한을 확인해 입력하세요.',
  'dismissal-report-art-18':
    '참고: 보건관리자를 해임하거나 위탁을 해지한 날부터 14일 이내 제출(산업안전보건법 시행령 제20조제3항 · 제16조제7항 준용). 실제 기한을 확인해 입력하세요.',
  'dismissal-report-art-22':
    '참고: 산업보건의를 해임하거나 해촉한 날부터 14일 이내 제출(산업안전보건법 시행령 제29조제4항). 실제 기한을 확인해 입력하세요.',
};

/**
 * 의무 목록 「이번 기간」 칸의 짧은 문구 + 근거(툴팁) — 기간을 정할 수 없는 사유별. 위 연간 격자 문구와
 * 같은 사유 집합이지만 **칸이 좁아** 라벨을 짧게 가르고 설명을 `hint`로 뺀다.
 */
export const CURRENT_PERIOD_SKIP_LABEL: Record<
  ObligationPeriodSkipReason,
  { label: string; hint: string }
> = {
  RECURRENCE_UNSET: {
    label: '주기 미정',
    hint: '이 문서의 주기가 아직 정해지지 않아 기간별 항목이 만들어지지 않습니다.',
  },
  EVENT_DRIVEN: {
    label: '사유 발생 시',
    hint: '정해진 기간이 없는 문서입니다. 사건이 생기면 「사건 등록」으로 사건일과 기한을 기록합니다.',
  },
  ONE_TIME: {
    label: '최초 1회',
    hint: '달력 기간이 없는 문서입니다. 한 번 기록하면 됩니다.',
  },
  STANDING_DUTY: {
    label: '상시',
    hint: '갖춰 두는 문서입니다. 정해진 주기가 없어 기간별 항목이 만들어지지 않습니다.',
  },
  ROLLING_NO_ANCHOR: {
    label: '마지막 실시일 확인 필요',
    hint: '기준일부터 주기가 도는 문서입니다. 이 현장에서 마지막으로 실시한 날(또는 설치·배치일)을 입력하면 다음 기한이 정해집니다.',
  },
  // 2026-11-04 — 보수교육의 기준일은 같은 사람의 신규교육 이수일이다(시행규칙 제29조①). 날짜를 짓지 않고 선행 의무의 기록을 기다린다
  PREREQUISITE_NOT_FULFILLED: {
    label: '신규교육 이수 후 정해집니다',
    hint: '이 문서의 기한은 같은 사람의 신규교육 이수일(또는 면제 성립일)부터 셉니다. 신규교육 문서나 면제를 등록하면 보수교육 기한이 정해집니다.',
  },
  CONSTRUCTION_PERIOD_UNKNOWN: {
    label: '공사 기간 미입력',
    hint: '현장의 공사 시작·종료일이 입력되지 않아 기간을 정할 수 없습니다.',
  },
  PERIOD_RULE_INVALID: {
    label: '주기 정보 확인 필요',
    hint: '이 문서의 주기 정보가 맞지 않습니다. 운영사가 카탈로그를 고치면 기간이 정해집니다.',
  },
};

/** 「기준일부터 도는 주기」인가 — 실시일·기산일 입력 입구를 그리는 술어(서버 `isRollingRule`과 같은 판정) */
export function isRollingRecurrence(rule: ObligationRecurrenceRule): boolean {
  return rule.recurrence === 'PERIODIC' && rule.intervalAnchor === 'ROLLING';
}

/** KSIC 코드 길이 → 분류 단계 이름(대분류 1글자 · 중분류 3 · 소분류 4 · 세분류 5 · 세세분류 6) */
function industryDepthLabel(depth: number): string {
  if (depth <= 1) return '대분류';
  if (depth <= 3) return '중분류';
  if (depth === 4) return '소분류';
  if (depth === 5) return '세분류';
  return '세세분류';
}

/**
 * 간격 변형의 「확인 필요」 문구(②-b D3-b ⓐ′) — 현장 사실(업종)이 없거나 얕아 **가장 짧은 간격을 보수 적용**했을 때.
 * 「무엇을 어느 깊이까지 넣으면 판정되나」를 말한다 — 입력 자리는 현장 정보(`/sites/:siteId/edit`)의 업종 칸이다.
 */
export function describeIntervalConfirmation(confirmation: IntervalVariantConfirmation): { label: string; hint: string } {
  const applied = formatObligationInterval(confirmation.appliedInterval);
  // 사람 사실(2026-11-04 · 직무 신규교육 「보건관리자가 의사인 경우 1년」) — 입력 입구는 조직 역할 화면의 직위 배정(의사 여부)
  if (confirmation.axis === 'PERSON_FACT') {
    return {
      label: '의사 여부 확인 필요',
      hint: `보건관리자가 의사인지 입력되지 않아 짧은 기한(${applied})을 적용했습니다. 조직 역할의 직위 배정에서 「의사 여부」를 고르면 기한이 정해집니다(의사면 1년).`,
    };
  }
  const depth = industryDepthLabel(confirmation.requiredDepth);
  if (confirmation.reason === 'FACT_MISSING') {
    return {
      label: '업종 확인 필요',
      hint: `현장 업종이 입력되지 않아 가장 짧은 간격(${applied})을 적용했습니다. 현장 정보에서 업종을 ${depth}까지 고르면 이 현장의 주기가 정해집니다.`,
    };
  }
  return {
    label: '업종 확인 필요',
    hint: `현장 업종이 ${depth}보다 얕게 입력되어 가장 짧은 간격(${applied})을 적용했습니다. 현장 정보에서 업종을 ${depth}까지 고르면 이 현장의 주기가 정해집니다.`,
  };
}

/**
 * 「이번 기간」 칸 — **인스턴스 행이 없을 때**의 표시. 종전엔 그 상태 전부가 「기간 없음」 한 문구라
 * 「주기가 없다」와 「주기는 있는데 기한이 아직 멀어 크론이 아직 안 만들었다」가 같은 말로 보였다
 * (2026-10-29 dev 실측 — 반기 의무의 하반기를 운영자가 「기간이 없다」로 읽었다). 가름의 재료는 서버가 싣는
 * `currentPeriod`(실체화 크론과 같은 `resolveObligationPeriod`)이고 여기서 기간을 다시 계산하지 않는다.
 *
 * - `skip` → 사유별 문구(위 표)
 * - `period` → 「항목 생성 전」 + 그 기간의 기한(`deadline`) — 행이 만들어지면 같은 기한을 갖는다
 *
 * ⛔ 수검업체 미등록 현장을 따로 가르지 않는다 — 실체화 크론은 미등록 현장에도 항목을 만든다(서버
 * `LegalObligationFlagMemo.isSiteEnabled`가 소유 조직의 플래그로 그 현장을 켜고, 실체화 서비스의
 * `loadActiveSites`가 수검업체 없는 현장을 포함한다). 종전의 「수검업체 미등록 — 항목이 만들어지지 않습니다」는
 * 전제가 바뀌어 **거짓**이 됐다(2026-10-29 · 줄기 9-t 형상 C 보강이 지목한 자리).
 *
 * 제외(`exclusion`)는 호출부가 이보다 **먼저** 그린다(제외가 기간 상태를 덮는다).
 */
export function describeMissingInstance(currentPeriod: LegalObligationCurrentPeriod): {
  label: string;
  hint: string;
  deadline: string | null;
} {
  if (currentPeriod.kind === 'skip') {
    return { ...CURRENT_PERIOD_SKIP_LABEL[currentPeriod.reason], deadline: null };
  }
  if (currentPeriod.kind === 'subjects') {
    // ②-c E4 — 사람 대상 의무는 행에 현장 전체의 기간이 없다(대상별 하위 줄이 기간을 든다)
    return { label: '대상별', hint: '직위 배정된 사람마다 기간이 따로 정해집니다. 아래 대상 줄을 보세요.', deadline: null };
  }
  const range = `${formatDateToLocal(new Date(currentPeriod.periodStart))} ~ ${formatDateToLocal(
    new Date(currentPeriod.periodEnd)
  )}`;
  return {
    label: '항목 생성 전',
    hint: `이번 기간(${range}) 항목은 기한이 가까워지면 자동으로 만들어집니다. 지금 문서를 등록해도 됩니다.`,
    deadline: currentPeriod.deadline,
  };
}

/**
 * 최근 측정·진단 결과 한 줄(②-c E7 · 서버 `latestResult`). 결과 이름은 그 의무의 변형 줄(`RESULT_FACT`의 `valueKeys` → `label`)에서
 * 찾고, 없으면 코드 그대로 보인다(숨기지 않는다).
 *
 * ## 「결과표를 등록하면 완료」 — 사용자 결정 2026-11-02
 * 결과 기록은 이행이 아니다 — **결과표(증빙)가 있어야 이행**이다. 그 결과의 결과표가 등록되지 않았으면(서버 `evidenceRegistered: false`)
 * 「측정 결과 기록됨 <날짜> · <결과>(<비율>) — 결과표를 등록하면 완료」를 보인다(`pendingEvidence: true`). 등록됐으면
 * 「최근 측정 <날짜> · <결과>(<비율>)」다. 결과표는 법정 증빙이라 **주기가 바뀌어도 등록 전까지 계속** 보인다.
 * ⛔ 판정을 여기서 하지 않는다 — 서버가 결과 단위로 판정해 싣는다(`LegalObligationResultView.evidenceRegistered`).
 *    종전엔 「측정일 ≥ 지금 기간 시작 ∧ 미이행」을 여기서 추론했는데, 노출기준 초과 결과가 기산을 ROLLING으로 바꾸면 기간이 측정일
 *    «다음날»부터 서서 미등록 결과가 「지난 기간」으로 밀려 안내가 사라졌다(2026-11-02 팀장 dev 실측).
 */
export function describeLatestResult(
  result: LegalObligationResultView,
  variants: ReadonlyArray<Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>>,
): { text: string; pendingEvidence: boolean } {
  const summary = describeMeasurementResult(result, variants);
  if (!result.evidenceRegistered) {
    return { text: `측정 결과 기록됨 ${summary} — 결과표를 등록하면 완료`, pendingEvidence: true };
  }
  return { text: `최근 측정 ${summary}`, pendingEvidence: false };
}

/**
 * 측정·진단 결과 하나를 「<측정일> · <결과 이름>(<비율>)」로(B+ — 결과표 등록 창의 선택지 · 증빙 목록의 「어느 측정의 결과표」).
 * 결과 이름은 `describeLatestResult`와 같은 규칙(변형 줄의 `label` · 없으면 코드 그대로)
 */
export function describeMeasurementResult(
  result: { code: string; occurredAt: string; exposureRatio?: number | null },
  variants: ReadonlyArray<Pick<LegalObligationIntervalVariantView, 'selector' | 'valueKeys' | 'label'>>,
): string {
  const label =
    variants.find((v) => v.selector === 'RESULT_FACT' && v.valueKeys.includes(result.code))?.label ?? result.code;
  const ratio = result.exposureRatio !== null && result.exposureRatio !== undefined ? `(${result.exposureRatio})` : '';
  return `${formatDateToLocal(new Date(result.occurredAt))} · ${label}${ratio}`;
}

export type AnnualCellTone = InstanceStatusTone | 'muted';

/**
 * 연간 격자 칸 하나의 표시 — L2의 「어디가 비었는지」가 이 함수의 판정이다.
 *
 * - 인스턴스가 있으면 **그 상태**(대기·지연·완료·늦은 완료 — 의무 목록과 같은 표).
 * - 인스턴스가 없고 기간이 **지났으면 「기록 없음」(rose)** — 그 칸이 빈 칸이다.
 *   ⚠️ 인스턴스는 기한이 다가와야 크론이 만든다(ⓘ 혼합). 그래서 기능이 켜지기 전의 지난 칸도 「기록 없음」이고,
 *   그것이 사실이다(그 기간의 이행 기록이 시스템에 없다).
 * - 지금 기간인데 아직 행이 없으면 「이번 기간」, 미래면 「예정」.
 */
export function describeAnnualCell(
  period: Pick<LegalObligationAnnualPeriod, 'timing' | 'instance'>
): { label: string; tone: AnnualCellTone } {
  if (period.instance) return INSTANCE_STATUS_LABEL[period.instance.status];
  switch (period.timing) {
    case 'PAST':
      return { label: '기록 없음', tone: 'rose' };
    case 'CURRENT':
      return { label: '이번 기간', tone: 'slate' };
    case 'FUTURE':
      return { label: '예정', tone: 'muted' };
  }
}

/**
 * 현장 목록의 수검업체 계정 표시(사용자 결정 2026-10-29) — 서버 `auditedCompanyHasAccount`를 그대로 읽는다
 * (판정 기준 「그 업체 조직의 구성원이 1명 이상」은 서버가 소유한다 — 여기서 다시 세지 않는다).
 * 수검업체가 없으면(`null`) 그리지 않는다 — 「수검업체가 아직 등록되지 않았습니다」가 이미 그 말을 한다.
 */
export function describeAuditedCompanyAccount(
  hasAccount: boolean | null
): { label: string; hint: string } | null {
  if (hasAccount === null) return null;
  return hasAccount
    ? {
        label: '계정 있음',
        hint: '수검업체가 SafetyHub 계정을 가지고 있어 이 현장의 문서를 직접 보고 업체 몫 알림을 받습니다.',
      }
    : {
        label: '계정 없음',
        hint: '수검업체에 SafetyHub 계정이 없어 이 현장의 문서는 관리 기관만 봅니다.',
      };
}

/** 「기한이 다가오는 문서」의 의무 묶음 하나 — `groupImminentByObligation`의 결과 */
export interface ImminentGroup<T> {
  obligationId: string;
  title: string;
  items: T[];
  /** 상태별 건수 — 서버가 `PENDING`·`OVERDUE`만 싣는다(shared `LegalDocumentImminentItem` doc) */
  counts: { PENDING: number; OVERDUE: number };
}

/**
 * 「기한이 다가오는 문서」를 **의무별로 묶는다**(사용자 결정 2026-10-29 — 의무 × 현장이 한 줄씩 반복되던 것).
 *
 * 순서는 **서버 순서를 보존**한다 — 서버가 기한 오름차순(지연이 앞)으로 주므로:
 * - 묶음 순서 = 그 의무의 **첫 출현** 순 = 가장 이른 기한 순. 지연 건이 있는 묶음은 기한이 이미 지났으니 앞에 온다.
 * - 묶음 안 순서 = 기한 오름차순(같은 기한이면 서버 순서).
 * 묶음 키는 `obligationId`다(제목은 카탈로그가 바꿀 수 있다 — 표시만 첫 행의 것을 쓴다).
 */
export function groupImminentByObligation<
  T extends { obligationId: string; title: string; instance: { status: ObligationInstanceStatus } },
>(items: readonly T[]): ImminentGroup<T>[] {
  const byId = new Map<string, ImminentGroup<T>>();
  for (const item of items) {
    let group = byId.get(item.obligationId);
    if (!group) {
      group = {
        obligationId: item.obligationId,
        title: item.title,
        items: [],
        counts: { PENDING: 0, OVERDUE: 0 },
      };
      byId.set(item.obligationId, group);
    }
    group.items.push(item);
    const status = item.instance.status;
    if (status === 'PENDING' || status === 'OVERDUE') group.counts[status] += 1;
  }
  return [...byId.values()];
}

/**
 * 묶음 머리의 건수 요약 — 「대기 3 · 지연 1」(사용자 결정 「건수 붙임」). 0건인 상태는 빼고, 지연을 뒤에 두지
 * 않고 **라벨 표 순서**(대기 → 지연)로 적는다. 라벨은 `INSTANCE_STATUS_LABEL`에서 온다(두 벌이면 갈린다).
 */
export function describeImminentCounts(counts: { PENDING: number; OVERDUE: number }): string {
  return (['PENDING', 'OVERDUE'] as const)
    .filter((s) => counts[s] > 0)
    .map((s) => `${INSTANCE_STATUS_LABEL[s].label} ${counts[s]}`)
    .join(' · ');
}

/**
 * 현장 목록의 관리 형태 문구 — 「직접 관리 / ○○ 대행 / 수검업체 미등록」(결정 ⓔ *고객 화면 입구* #2).
 *
 * `AGENCY`의 ○○은 **대행 기관**이다 — 그런데 2026-10-31 재개정 (다) 이후 현장 소유 조직은 **업체**라
 * `ownerCompanyName`을 그대로 쓰면 링크 기반 대행 현장에서 「업체 이름 대행」이 찍힌다(P4 화면 몫 결함). 그래서:
 * 1. 서버가 실은 기관 이름(`LegalDocumentSiteSummary.agencyCompanyName` — 첫 대행 기관 · 옛 형태는 소유 조직 이름)이면 그것
 *    (계약은 optional이다 — web 픽스처 다수가 형상을 손으로 지어서. 서버는 늘 싣는다)
 * 2. 없으면 소유 조직 ≠ 수검업체(과도기 옛 형태 — 소유 조직이 기관)일 때만 소유 조직 이름
 * 3. 그 밖(소유 조직 = 수검업체 — 링크 기반)은 이름 없이 「대행 관리」 — 업체 이름을 기관처럼 찍지 않는다
 */
export function describeSiteManagement(
  site: Pick<LegalDocumentSiteSummary, 'management' | 'ownerCompanyName'> &
    Partial<Pick<LegalDocumentSiteSummary, 'auditedCompanyName' | 'agencyCompanyName'>>
): string {
  switch (site.management) {
    case 'SELF':
      return '직접 관리';
    case 'AGENCY':
      if (site.agencyCompanyName) return `${site.agencyCompanyName} 대행`;
      if (site.auditedCompanyName != null && site.ownerCompanyName !== site.auditedCompanyName) {
        return `${site.ownerCompanyName} 대행`;
      }
      return '대행 관리';
    case 'UNREGISTERED':
      return '수검업체 미등록';
  }
}

/**
 * 이행률(L20) — 「이번 기간 대상」 중 이행(제때 + 늦은 완료)의 백분율, 정수 반올림. 분모 0이면 `null`
 * (「0%」로 그리면 「하나도 안 했다」로 읽힌다 — 대상이 없는 것과 다르다). 통의 정의는 shared
 * `LegalDocumentComplianceCounts` docblock이 정본이다.
 */
export function complianceRatePercent(
  counts: Pick<LegalDocumentComplianceCounts, 'target' | 'fulfilledOnTime' | 'fulfilledLate'>
): number | null {
  if (counts.target <= 0) return null;
  return Math.round(((counts.fulfilledOnTime + counts.fulfilledLate) / counts.target) * 100);
}

/**
 * 이행률 요약의 내역 한 줄 — 「완료 3 · 늦은 완료 1 · 지연 1 · 대기 2 · 생성 전 4」. 0건인 통은 뺀다.
 * 상태 라벨은 `INSTANCE_STATUS_LABEL`에서 온다(두 벌이면 목록 화면과 갈린다).
 */
export function describeComplianceBreakdown(counts: LegalDocumentComplianceCounts): string {
  const parts: Array<[string, number]> = [
    [INSTANCE_STATUS_LABEL.FULFILLED.label, counts.fulfilledOnTime],
    [INSTANCE_STATUS_LABEL.FULFILLED_LATE.label, counts.fulfilledLate],
    [INSTANCE_STATUS_LABEL.OVERDUE.label, counts.overdue],
    [INSTANCE_STATUS_LABEL.PENDING.label, counts.pending],
    ['항목 생성 전', counts.notCreated],
  ];
  return parts
    .filter(([, n]) => n > 0)
    .map(([label, n]) => `${label} ${n}`)
    .join(' · ');
}
